import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Boxes, FileText, Play, RotateCw, Square } from "lucide-react";
import { deployComposeAction, deployComposePs } from "../../redux/actionCreator";

/**
 * Per-service status and controls for a compose project.
 *
 * `ps --all` so a crashed service still has a row — a stack where the
 * broken service simply vanishes from the list is the worst version of
 * this screen.
 *
 * Rebuilds are not here. `up -d` recreates from the current image, which
 * is quick; building from source takes minutes and belongs in the wizard,
 * which streams its output into a job.
 */
const REFRESH_MS = 5000;

function StateBadge({ s }) {
  const state = (s.state || "").toLowerCase();
  const health = (s.health || "").toLowerCase();
  let cls = "bg-secondary";
  let text = s.state || "unknown";
  if (state === "running") {
    if (health === "healthy") { cls = "bg-success"; text = "running · healthy"; }
    else if (health === "unhealthy") { cls = "bg-danger"; text = "running · UNHEALTHY"; }
    else if (health === "starting") { cls = "bg-info text-dark"; text = "starting"; }
    else { cls = "bg-success"; text = "running"; }
  } else if (state === "exited") {
    // A clean exit is normal for a one-shot; a non-zero one is not.
    cls = s.exit_code === 0 ? "bg-secondary" : "bg-danger";
    text = s.exit_code === 0 ? "exited (0)" : `exited (${s.exit_code})`;
  } else if (state === "restarting") {
    cls = "bg-warning text-dark"; text = "restarting";
  }
  return <span className={`badge ${cls}`}>{text}</span>;
}

export default function ComposeStatusModal({ show, onHide, projectId, serverId, initialRepoDir, initialFile }) {
  const dispatch = useDispatch();
  const [repoDir, setRepoDir] = useState(initialRepoDir || "");
  const [file, setFile] = useState(initialFile || "docker-compose.yml");
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busyOn, setBusyOn] = useState(null); // "svc:action" currently running
  const [logs, setLogs] = useState(null);     // { service, text }
  const timerRef = useRef(null);
  const logRef = useRef(null);

  const load = useCallback(async (quiet) => {
    if (!repoDir.trim()) return;
    if (!quiet) { setLoading(true); setErr(null); }
    const res = await dispatch(deployComposePs(projectId, {
      server_id: serverId, repo_dir: repoDir.trim(), file: file.trim(),
    }));
    if (!quiet) setLoading(false);
    if (res?.status) { setServices(res.data.services || []); setErr(null); }
    else if (!quiet) setErr(res?.message || "Could not read status");
  }, [dispatch, projectId, serverId, repoDir, file]);

  // Poll while open so a restart is visible without clicking Refresh.
  // Quiet refreshes don't clear the table or flash a spinner.
  useEffect(() => {
    if (!show || !services) return undefined;
    timerRef.current = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [show, services, load]);

  useEffect(() => () => clearInterval(timerRef.current), []);
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [logs]);

  const run = async (action, service) => {
    if (action === "down" && !window.confirm(
      "Stop and REMOVE every container in this project?\n\nNamed volumes are kept; containers and the network are not.",
    )) return;
    setBusyOn(`${service || "*"}:${action}`);
    const res = await dispatch(deployComposeAction(projectId, {
      server_id: serverId, repo_dir: repoDir.trim(), file: file.trim(), action, service,
    }));
    setBusyOn(null);
    if (action === "logs") {
      setLogs({ service: service || "all", text: res?.data?.output || res?.message || "(no output)" });
    } else if (!res?.status) {
      setErr(`${res?.message || "Action failed"}${res?.data?.output ? `\n${res.data.output.slice(-800)}` : ""}`);
    } else {
      setErr(null);
    }
    load(true);
  };

  const anyBusy = !!busyOn;

  return (
    <Modal show={show} onHide={anyBusy ? undefined : onHide} size="xl" centered>
      <Modal.Header closeButton={!anyBusy}>
        <h6 className="m-0 d-inline-flex align-items-center gap-2">
          <Boxes size={16} /> Compose status
        </h6>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-6">
            <Form.Label className="mb-1">Repo directory</Form.Label>
            <Form.Control size="sm" value={repoDir} onChange={(e) => setRepoDir(e.target.value)}
              placeholder="/home/drc" style={{ fontFamily: "ui-monospace, monospace" }} />
          </div>
          <div className="col-md-4">
            <Form.Label className="mb-1">Compose file</Form.Label>
            <Form.Control size="sm" value={file} onChange={(e) => setFile(e.target.value)}
              style={{ fontFamily: "ui-monospace, monospace" }} />
          </div>
          <div className="col-md-2">
            <Button size="sm" variant="light" className="w-100"
              onClick={() => load(false)} disabled={loading || !repoDir.trim()}>
              {loading ? <Spinner size="sm" animation="border" /> : "Load"}
            </Button>
          </div>
        </div>

        {err && <pre className="alert alert-danger py-2 small mb-3" style={{ whiteSpace: "pre-wrap" }}>{err}</pre>}

        {services && (
          <>
            <div className="d-flex gap-2 mb-2 flex-wrap">
              <Button size="sm" variant="success" disabled={anyBusy} onClick={() => run("up")}>
                <Play size={13} className="me-1" /> Up
              </Button>
              <Button size="sm" variant="light" disabled={anyBusy} onClick={() => run("pull")}>Pull</Button>
              <Button size="sm" variant="light" disabled={anyBusy} onClick={() => run("logs")}>
                <FileText size={13} className="me-1" /> All logs
              </Button>
              <Button size="sm" variant="outline-danger" disabled={anyBusy} onClick={() => run("down")}>Down</Button>
              <span className="ms-auto small text-muted align-self-center">
                refreshing every {REFRESH_MS / 1000}s
              </span>
            </div>

            <table className="emp-table">
              <thead>
                <tr>
                  <th>Service</th><th>State</th><th>Ports</th><th>Uptime</th><th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.length === 0 && (
                  <tr><td colSpan={5} className="text-muted small">
                    No containers for this project yet — press Up, or deploy from the wizard.
                  </td></tr>
                )}
                {services.map((s) => (
                  <tr key={s.service}>
                    <td><code className="small">{s.service}</code></td>
                    <td><StateBadge s={s} /></td>
                    <td className="small">
                      {s.ports.length
                        ? s.ports.map((p) => <code key={p.published} className="me-2">{p.published}→{p.target}</code>)
                        : <span className="text-muted">—</span>}
                    </td>
                    <td className="small text-muted">{s.status || "—"}</td>
                    <td className="text-end">
                      <div className="d-inline-flex gap-1">
                        <Button size="sm" variant="light" disabled={anyBusy}
                          title="Restart this service" onClick={() => run("restart", s.service)}>
                          {busyOn === `${s.service}:restart`
                            ? <Spinner size="sm" animation="border" />
                            : <RotateCw size={13} />}
                        </Button>
                        {s.running ? (
                          <Button size="sm" variant="light" disabled={anyBusy}
                            title="Stop this service" onClick={() => run("stop", s.service)}>
                            <Square size={13} />
                          </Button>
                        ) : (
                          <Button size="sm" variant="light" disabled={anyBusy}
                            title="Start this service" onClick={() => run("start", s.service)}>
                            <Play size={13} />
                          </Button>
                        )}
                        <Button size="sm" variant="light" disabled={anyBusy}
                          title="Last 200 log lines" onClick={() => run("logs", s.service)}>
                          <FileText size={13} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {logs && (
              <div className="mt-3">
                <div className="d-flex align-items-center mb-1">
                  <strong className="small">logs — {logs.service}</strong>
                  <Button size="sm" variant="link" className="ms-auto p-0" onClick={() => setLogs(null)}>hide</Button>
                </div>
                <pre ref={logRef} className="small mb-0"
                  style={{ maxHeight: 300, overflow: "auto", whiteSpace: "pre-wrap" }}>
                  {logs.text}
                </pre>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide} disabled={anyBusy}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
